import * as React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { StyleSheet } from '@react-pdf/renderer';
import { PDF_FONT_SIZES, PDF_COLORS, PDF_SPACING } from '../styles/pdfStyles';
import { PDF_FONTS } from '../styles/fonts';
import { formatBirthdateForPDF, formatDateForPDF, formatPatientName } from '../utils/dataFormatters';
import { createT } from '../utils/simpleTranslate';
import { getPatientID, getHealthIDLabel } from '../../../utils/index.jsx';

/**
 * Fixed metadata footer for PDFs that contain patient info.
 * Renders at the bottom of every page with patient line, clinic, validated by, document ID, date, confidentiality, and page numbers.
 */

export interface PDFMetadataFooterData {
  patient: {
    personalData?: {
      name?: string;
      surnames?: string;
      birthdate?: string | Date | number;
    };
    id?: string | number;
  };
  documentReference: string;
  documentDate: string | Date;
  validatedBy?: { name: string; title?: string } | null;
  locale: string;
  hospitalName: string;
  city: string;
}

const footerTextStyle = {
  fontSize: PDF_FONT_SIZES.CAPTION,
  color: PDF_COLORS.TEXT,
  fontFamily: PDF_FONTS.PRIMARY,
};

const fixedFooterStyles = StyleSheet.create({
  wrapper: {
    position: 'absolute' as const,
    left: 30,
    right: 30,
    bottom: 20,
    borderTopWidth: 1,
    borderTopColor: PDF_COLORS.BORDER,
    paddingTop: PDF_SPACING.SM,
    flexDirection: 'column',
  },
  line: footerTextStyle,
  row: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'flex-start' as const,
    marginTop: 2,
  },
  pageNumber: {
    ...footerTextStyle,
    textAlign: 'right' as const,
  },
});

export const PDFMetadataFooter: React.FC<{
  data: PDFMetadataFooterData;
}> = ({ data }) => {
  const {
    patient,
    documentReference,
    documentDate,
    validatedBy,
    locale,
    hospitalName,
    city,
  } = data;

  const t = createT(locale);

  const patientName = patient?.personalData
    ? formatPatientName(patient.personalData)
    : '';
  const dob = patient?.personalData?.birthdate
    ? formatBirthdateForPDF(patient.personalData.birthdate, locale)
    : '';
  const healthIdLabel = patient?.personalData ? t(getHealthIDLabel(patient)) : '';
  const healthIdValue = patient?.personalData ? getPatientID(patient) : '';

  const line1Parts = [
    patientName && `${t('general.patient')}: ${patientName}`,
    dob && `${t('investigation.create.personal_data.short-fields.birthdate')}: ${dob}`,
    healthIdLabel && healthIdValue && `${healthIdLabel}: ${healthIdValue}`,
  ].filter(Boolean);
  const line1 = line1Parts.join(' | ');

  const line2Parts = [
    `${hospitalName} - ${city}`,
    validatedBy?.name &&
      `${t('hospital.document.validated_by')}: ${validatedBy.name}${validatedBy.title ? `, ${validatedBy.title}` : ''}`,
  ].filter(Boolean);
  const line2 = line2Parts.join(' | ');

  const line3Parts = [
    `${t('hospital.document.document_id')}: ${documentReference}`,
    `${t('general.date')}: ${formatDateForPDF(documentDate, locale)}`,
    t('hospital.document.confidential'),
  ].filter(Boolean);
  const line3 = line3Parts.join(' | ');

  return (
    <View fixed style={fixedFooterStyles.wrapper}>
      {line1 ? (
        <Text style={fixedFooterStyles.line}>{line1}</Text>
      ) : null}
      <Text style={fixedFooterStyles.line}>{line2}</Text>
      <Text style={fixedFooterStyles.line}>{line3}</Text>
      <View style={fixedFooterStyles.row}>
        <View style={{ flex: 1 }} />
        <Text
          style={fixedFooterStyles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${t('general.page')} ${pageNumber} ${t('general.of')} ${totalPages}`
          }
        />
      </View>
    </View>
  );
};

export default PDFMetadataFooter;
